import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import {
  HeartOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
  MinusOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Rating from "react-rating";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "aos/dist/aos.css";
import ProductDetailSkeleton from "../components/ProductDetailSkeleton";
import { useNavigate, useLocation, useParams } from "react-router";
import ProductSection from "../components/ProductSection";

const Container = styled.div`
  background-color: #ffffff;
  min-height: 100vh;
`;

const ProductWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const ProductContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 60px;
  align-items: flex-start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ImageGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  height: fit-content;

  @media (max-width: 768px) {
    position: static;
    max-height: none;
    height: auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition:
      opacity 0.5s ease-in-out,
      transform 0.5s ease-in-out;
    animation: fadeIn 0.5s ease-in-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 768px) {
    height: 350px;
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  padding: 5px 0;
  flex: 1;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const ImageSliderWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  overflow: visible;
  padding: 0 50px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 40px;
  }
`;

const ArrowButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: white;
  color: #000;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;

  &:first-of-type {
    left: 10px;
  }

  &:last-of-type {
    right: 10px;
  }

  &:hover {
    background: #fd8e07;
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: white;
      color: #000;
      transform: scale(1);
    }
  }
`;

const Thumbnail = styled.div`
  width: 120px;
  height: 120px;
  min-width: 120px;
  min-height: 120px;
  background-color: #f5f5f5;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${(props) => (props.$active ? "#FD8E07" : "#ddd")};
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$active ? "1" : "0.6")};
  box-shadow: ${(props) =>
    props.$active ? "0 4px 12px rgba(0, 0, 0, 0.15)" : "none"};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &:hover {
    border-color: #fd8e07;
    opacity: 1;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProductTitle = styled.h1`
  font-size: 25px;
  font-weight: 700;
  color: #000;
  margin: 0;
  text-transform: capitalize;
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const RatingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  color: #ffc107;
  font-size: 16px;
  margin-left: -5px;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 20px;
  padding: 15px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const MetaItem = styled.div`
  font-size: 14px;
  color: #666;
  text-transform: capitalize;
  span {
    font-weight: 600;
    color: #000;
  }
`;

const Description = styled.div`
  color: #666;
  line-height: 1.6;
  font-size: 14px;
  margin: 0;
`;

const SelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0;
`;

const SelectorGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SelectorLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #000;
`;

const SizeSelector = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SizeButton = styled.button`
  padding: 10px 18px;
  border: 2px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    border-color: #fd8e07;
  }

  ${(props) =>
    props.$active &&
    `
    border-color: #FD8E07;
    background: #FD8E07;
    color: white;
  `}
`;

const ColorSelector = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ColorButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${(props) => props.$color};

  &:hover {
    border-color: #fd8e07;
    transform: scale(1.1);
  }

  ${(props) =>
    props.$active &&
    `
    border-color: #FD8E07;
    box-shadow: 0 0 0 3px #fff, 0 0 0 5px #FD8E07;
  `}
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 0;
`;

const Price = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: #000;
`;

const OriginalPrice = styled.span`
  font-size: 16px;
  color: #999;
  text-decoration: line-through;
`;

const DeliveryInfo = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0;
`;

const QuantityWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: fit-content;
`;

const QuantityButton = styled.button`
  background: transparent;
  border: none;
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    color: #fd8e07;
  }
`;

const QuantityInput = styled.input`
  border: none;
  width: 50px;
  text-align: center;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;

  &:focus {
    outline: none;
  }
`;

const AddToCartButton = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 15px 40px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;

  &:hover:not(:disabled) {
    background: #fd8e07;
  }

  &:disabled {
    background: #dc3545;
    cursor: not-allowed;
    opacity: 1;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CartMessage = styled.div`
  padding: 14px 16px;
  border-radius: 4px;
  margin: 15px 0;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${(props) =>
    props.$type === "success"
      ? `
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : `
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const LoaderSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const OutOfStockMessage = styled.div`
  margin-top: 15px;
  padding: 6px 12px;
  background: rgba(220, 53, 69, 0.95);
  color: white;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: slideDown 0.3s ease;
  width: fit-content;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin: 20px 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: #fd8e07;
    color: #fd8e07;
  }
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 10px;
  margin: 20px 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const ShareButton = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &.facebook {
    background: #3b5998;
    color: white;
  }

  &.twitter {
    background: #1da1f2;
    color: white;
  }

  &.pinterest {
    background: #e60023;
    color: white;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const PolicySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 30px 0;
  padding: 20px 0;
  border-top: 1px solid #eee;
`;

const PolicyItem = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-start;

  svg {
    width: 20px;
    height: 20px;
    color: #fd8e07;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #000;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #666;
  }
`;

const TabsWrapper = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #eee;
`;

const TabsHeader = styled.div`
  display: flex;
  gap: 30px;
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${(props) => (props.$active ? "#000" : "#999")};
  border-bottom: 3px solid
    ${(props) => (props.$active ? "#000" : "transparent")};
  transition: all 0.3s ease;

  &:hover {
    color: #000;
  }
`;

const TabContent = styled.div`
  display: ${(props) => (props.$active ? "block" : "none")};

  ul {
    padding-left: 20px;
    color: #666;
    line-height: 1.8;
    font-size: 14px;

    li {
      margin-bottom: 10px;
    }
  }

  p {
    color: #666;
    line-height: 1.8;
    font-size: 14px;
  }
`;

const ReviewsSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #eee;
`;

const ReviewsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;

  h2 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #000;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const WriteReviewButton = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 12px 25px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #fd8e07;
  }
`;

const ReviewItem = styled.div`
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-color: #fd8e07;
  }

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 15px;
  }
`;

const ReviewMeta = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ReviewRating = styled.div`
  display: flex;
  gap: 2px;
  color: #ffc107;
  font-size: 16px;
  align-items: center;

  span {
    line-height: 1;
  }
`;

const ReviewLabel = styled.p`
  margin: 0;
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const ReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
`;

const ReviewTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const ReviewText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #555;
  line-height: 1.7;
  letter-spacing: 0.3px;
`;

const ReviewAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
`;

const AuthorAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fd8e07, #fda01e);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;

  p {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #000;
  }

  span {
    font-size: 12px;
    color: #999;
  }
`;

const RelatedSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #eee;
`;

const ModalOverlay = styled.div`
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  transition: color 0.3s ease;

  &:hover {
    color: #000;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #000;
`;

const RatingSelector = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  span {
    cursor: pointer;
    font-size: 28px;
    transition: transform 0.2s ease;
    color: #ddd;

    &:hover {
      transform: scale(1.2);
    }

    &.active {
      color: #ffc107;
    }
  }
`;

const ReviewTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #fd8e07;
  }

  &::placeholder {
    color: #999;
  }
`;

const ImageUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.label`
  padding: 10px 16px;
  border: 2px dashed #ddd;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  font-weight: 500;
  color: #666;

  &:hover {
    border-color: #fd8e07;
    color: #fd8e07;
  }
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: red;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.2s ease;

  &:hover {
    background: darkred;
    transform: scale(1.1);
  }
`;

const DeleteReviewButton = styled.button`
  border: none;
  padding: 8px 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: white;
  font-weight: 600;
  border-radius: 4px;
  white-space: nowrap;
  color: black;
  &:hover:not(:disabled) {
    background: #fc8c04;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 13px;
  }
`;

const ReviewImagesContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  margin-bottom: 8px;
`;

const ReviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #f0f0f0;

  &:hover {
    border-color: #fd8e07;
    transform: scale(1.05);
  }
`;

const ShowAllReviewsButton = styled.button`
  background: #fd8e07;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  width: 100%;
  transition: all 0.3s ease;

  &:hover {
    background: #fc8c04;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(253, 142, 7, 0.3);
  }
`;

const SlideshowContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .slideshow-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 450px;
    padding: 20px;
    background: #f5f5f5;
  }

  .slide-image {
    max-width: 350px;
    max-height: 350px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
  }

  .nav-buttons {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.6);
    border: none;
    color: white;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    font-weight: bold;
    transition: all 0.3s ease;
    z-index: 10;

    &:hover {
      background: rgba(0, 0, 0, 0.9);
      transform: translateY(-50%) scale(1.1);
    }

    &:active {
      transform: translateY(-50%) scale(0.95);
    }
  }

  .nav-prev {
    left: 15px;
  }

  .nav-next {
    right: 15px;
  }

  .slide-counter {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
  }

  .dot-indicators {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }

    &.active {
      background: #fd8e07;
    }
  }

  /* Tablet responsiveness */
  @media (max-width: 768px) {
    max-width: 85%;

    .slideshow-wrapper {
      height: 320px;
      padding: 12px;
    }

    .slide-image {
      max-width: 250px;
      max-height: 250px;
    }

    .nav-buttons {
      width: 36px;
      height: 36px;
      font-size: 16px;
    }

    .nav-prev {
      left: 8px;
    }

    .nav-next {
      right: 8px;
    }

    .slide-counter {
      font-size: 11px;
      padding: 5px 10px;
      bottom: 12px;
    }

    .dot {
      width: 9px;
      height: 9px;
    }
  }

  /* Mobile responsiveness */
  @media (max-width: 600px) {
    max-width: 100%;
    margin: 0;
    border-radius: 0;

    .slideshow-wrapper {
      height: 240px;
      padding: 8px;
    }

    .slide-image {
      max-width: 180px;
      max-height: 180px;
    }

    .nav-buttons {
      width: 30px;
      height: 30px;
      font-size: 14px;
    }

    .nav-prev {
      left: 6px;
    }

    .nav-next {
      right: 6px;
    }

    .slide-counter {
      font-size: 10px;
      padding: 4px 8px;
      bottom: 10px;
    }

    .dot {
      width: 7px;
      height: 7px;
      gap: 4px;
    }
  }

  /* Small mobile devices */
  @media (max-width: 400px) {
    .slideshow-wrapper {
      height: 200px;
      padding: 6px;
    }

    .slide-image {
      max-width: 150px;
      max-height: 150px;
    }

    .nav-buttons {
      width: 28px;
      height: 28px;
      font-size: 12px;
    }

    .nav-prev {
      left: 4px;
    }

    .nav-next {
      right: 4px;
    }

    .slide-counter {
      font-size: 9px;
      padding: 3px 6px;
      bottom: 8px;
    }

    .dot {
      width: 6px;
      height: 6px;
    }
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 25px;
  padding-top: 15px;
  border-top: 1px solid #eee;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(ModalButton)`
  background: #000;
  color: white;

  &:hover:not(:disabled) {
    background: #fd8e07;
  }
`;

const CancelButton = styled(ModalButton)`
  background: #f0f0f0;
  color: #000;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 30px;
  text-align: center;
`;

const RelatedProducts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ProductCard = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ProductCardImage = styled.div`
  width: 100%;
  height: 250px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ProductCardInfo = styled.div`
  padding: 15px;
`;

const ProductCardTitle = styled.p`
  margin: 0 0 10px 0;
  font-size: 13px;
  font-weight: 600;
  color: black;
  height: 32px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProductCardRating = styled.div`
  display: flex;
  gap: 3px;
  color: black;
  font-size: 12px;
  margin-bottom: 8px;
`;

const ProductCardPrice = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: black;
`;

const ProductDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const productFromState = location.state.product;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get token from Redux
  const token = useSelector((state) => state.token);

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("S");
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [cartMessage, setCartMessage] = useState(null);
  const [cartMessageType, setCartMessageType] = useState("success");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [showAllReviewsModal, setShowAllReviewsModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewImages, setReviewImages] = useState([]);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistId, setWishlistId] = useState(null);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [isDeletingReview, setIsDeletingReview] = useState(null);
  const [previewedReviewImages, setPreviewedReviewImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const userId = useSelector((state) => state.userId);

  // Fetch reviews from API
  const fetchReviews = async (productId) => {
    try {
      setIsLoadingReviews(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/products/${productId}/reviews`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();

      if (data.status && Array.isArray(data.data)) {
        setReviews(data.data);
      } else {
        setReviews([]);
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setReviews([]);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  // Delete review
  const handleDeleteReview = async (reviewId, productId) => {
    if (!token) {
      navigate("/login", {
        state: { from: location.pathname, product: productFromState },
      });
      return;
    }

    Modal.confirm({
      title: "Delete Review",
      content:
        "Are you sure you want to delete this review? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setIsDeletingReview(reviewId);
          const apiBase = process.env.REACT_APP_API_BASE_URL;
          const response = await fetch(
            `${apiBase}/products/${productId}/reviews/${reviewId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          const data = await response.json();

          if (data.status) {
            // Remove review from list
            setReviews((prev) => prev.filter((r) => r.id !== reviewId));
            alert("Review deleted successfully");
          } else {
            alert(data.message || "Failed to delete review");
          }
        } catch (err) {
          console.error("Error deleting review:", err);
          alert("Error deleting review. Please try again.");
        } finally {
          setIsDeletingReview(null);
        }
      },
      onCancel() {},
    });
  };

  const openImagePreview = (images, initialIndex = 0) => {
    setPreviewedReviewImages(images);
    setSelectedImageIndex(initialIndex);
    setIsImagePreviewOpen(true);
  };

  const fetchCart = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/cart`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.status && data.data?.items) {
        // Set cart count based on number of items
        // setCartCount(data.data.items.length);
        dispatch({ type: "updateCartCount", count: data.data.items.length });
      } else {
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
    }
  };

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        // Use id from URL params (more reliable than state)

        const response = await fetch(
          `${apiBase}/products/${productFromState.id}`,
        );
        const data = await response.json();

        if (data.status && data.data) {
          setProduct(data.data);
          // Set default size and color
          const sizes = Object.keys(data.data.variants || {});
          if (sizes.length > 0) {
            setSelectedSize(sizes[0]);
            const firstColor = data.data.variants[sizes[0]]?.[0];
            if (firstColor) {
              setSelectedColorId(firstColor.id);
            }
          }
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching product:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (productFromState?.id) {
      fetchProduct();
      // Fetch reviews when product changes
      fetchReviews(productFromState.id);
    }
  }, [productFromState]);

  // Fetch wishlist on component mount
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!token) return;
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${apiBase}/wishlist`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.status && data.data) {
          setWishlistItems(data.data);
          // Check if current product is in wishlist
          const found = data.data.find(
            (item) => item.product.id === productFromState.id,
          );
          if (found) {
            setIsWishlisted(true);
            setWishlistId(found.id);
          }
        }
      } catch (err) {
        console.error("Error fetching wishlist:", err);
      }
    };

    if (token && productFromState?.id) {
      fetchWishlist();
    }
  }, [token, productFromState?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productImages = product?.images?.map((img) => img.url) || [];
  const colors =
    product?.variants?.[selectedSize]?.map((v) => v.color_hex_code) || [];
  const availableSizes = Object.keys(product?.variants || {});

  // Related products - can be fetched from API or left as empty for now
  const relatedProducts = [];

  const handleQuantityChange = (value) => {
    if (value > 0) setQuantity(value);
  };

  const handleAddToCart = async () => {
    // Check if user is logged in
    if (!token) {
      navigate("/login", {
        state: { from: location.pathname, product: productFromState },
      });
      return;
    }

    // Get selected variant
    const selectedVariant = product?.variants?.[selectedSize]?.find(
      (v) => v.id === selectedColorId,
    );

    if (!selectedVariant) {
      setCartMessage("Please select a color");
      setCartMessageType("error");
      return;
    }

    if (selectedVariant.stock_qty === 0) {
      setCartMessage("This item is out of stock");
      setCartMessageType("error");
      return;
    }

    try {
      setIsCartLoading(true);
      setCartMessage(null);

      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(
        `${apiBase}/cart/items?product_variant_id=${selectedVariant.id}&quantity=${quantity}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.status === true) {
        // fetchCart();
        setCartMessage("✓ Added to cart successfully!");

        setCartMessageType("success");
        setQuantity(1); // Reset quantity
        setTimeout(() => setCartMessage(null), 3000); // Clear message after 3 seconds
      } else if (
        data.status === false &&
        data.message === "Insufficient stock"
      ) {
        setCartMessage(
          `Insufficient stock. Available: ${data.data?.available_qty || 0} item(s)`,
        );
        setCartMessageType("error");
      } else {
        setCartMessage(data.message || "Failed to add to cart");
        setCartMessageType("error");
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      setCartMessage("Error adding to cart. Please try again.");
      setCartMessageType("error");
    } finally {
      setIsCartLoading(false);
    }
  };

  const handlePreviousImage = () => {
    if (!productImages.length) return;
    setSelectedImage((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1,
    );
  };

  const handleNextImage = () => {
    if (!productImages.length) return;
    setSelectedImage((prev) =>
      prev === productImages.length - 1 ? 0 : prev + 1,
    );
  };

  const handleImageUploadChange = (e) => {
    const files = Array.from(e.target.files);
    setReviewImages((prev) => [...prev, ...files]);
    e.target.value = ""; // Reset input
  };

  const handleRemoveImage = (index) => {
    setReviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddToWishlist = async () => {
    if (!token) {
      navigate("/login", {
        state: { from: location.pathname, product: productFromState },
      });
      return;
    }

    try {
      setIsWishlistLoading(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(
        `${apiBase}/wishlist?product_id=${product.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.status) {
        setIsWishlisted(true);
        setWishlistId(data.data.id);
        setCartMessage("✓ Added to wishlist!");
        setCartMessageType("success");
        setTimeout(() => setCartMessage(null), 2000);
      } else {
        setCartMessage(data.message || "Failed to add to wishlist");
        setCartMessageType("error");
      }
    } catch (err) {
      console.error("Error adding to wishlist:", err);
      setCartMessage("Error adding to wishlist");
      setCartMessageType("error");
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const handleRemoveFromWishlist = async () => {
    if (!wishlistId) return;

    try {
      setIsWishlistLoading(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/wishlist/${product.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.status) {
        setIsWishlisted(false);
        setWishlistId(null);
        setCartMessage("✓ Removed from wishlist");
        setCartMessageType("success");
        setTimeout(() => setCartMessage(null), 2000);
      } else {
        setCartMessage(data.message || "Failed to remove from wishlist");
        setCartMessageType("error");
      }
    } catch (err) {
      console.error("Error removing from wishlist:", err);
      setCartMessage("Error removing from wishlist");
      setCartMessageType("error");
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) {
      alert("Please write a review");
      return;
    }

    if (!token) {
      navigate("/login", {
        state: { from: location.pathname, product: productFromState },
      });
      return;
    }

    try {
      setIsSubmittingReview(true);

      const formData = new FormData();
      formData.append("rating", reviewRating);
      formData.append("comment", reviewText);

      // Append images as files
      reviewImages.forEach((image) => {
        formData.append("images[]", image);
      });

      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(
        `${apiBase}/products/${product.id}/reviews`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (data.status) {
        alert("Review submitted successfully!");

        // Reset form
        setReviewText("");
        setReviewRating(5);
        setReviewImages([]);
        setIsReviewModalOpen(false);

        // Refresh reviews
        if (product?.id) {
          fetchReviews(product.id);
        }
      } else {
        alert(data.message || "Failed to submit review");
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      alert("Error submitting review. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const getDisplayImages = () => {
    if (!productImages.length) return [];
    if (productImages.length === 1) return [0];
    if (productImages.length === 2) return [0, 1];

    const prev =
      selectedImage === 0 ? productImages.length - 1 : selectedImage - 1;
    const next =
      selectedImage === productImages.length - 1 ? 0 : selectedImage + 1;
    return [prev, selectedImage, next];
  };

  if (loading) {
    return (
      <Container>
        {/* <Navbar /> */}
        <ProductDetailSkeleton />
        <Footer />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container>
        {/* <Navbar /> */}
        <ProductWrapper>
          <h2 style={{ textAlign: "center", padding: "60px 20px" }}>
            {error ? `Error: ${error}` : "Product not found"}
          </h2>
        </ProductWrapper>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      {/* <Navbar /> */}

      <ProductWrapper>
        <ProductContent>
          <ImageGallery>
            <MainImage>
              <img src={productImages[selectedImage]} alt="Product" />
            </MainImage>
            {productImages.length > 1 && (
              <ImageSliderWrapper>
                <ArrowButton
                  onClick={handlePreviousImage}
                  title="Previous image"
                >
                  ←
                </ArrowButton>
                <ThumbnailContainer data-thumbnail-container>
                  {getDisplayImages().map((imgIdx, idx) => (
                    <Thumbnail
                      key={`thumb-${idx}-${imgIdx}`}
                      $active={selectedImage === imgIdx}
                      onClick={() => setSelectedImage(imgIdx)}
                    >
                      <img
                        src={productImages[imgIdx]}
                        alt={`Thumbnail ${imgIdx + 1}`}
                      />
                    </Thumbnail>
                  ))}
                </ThumbnailContainer>
                <ArrowButton onClick={handleNextImage} title="Next image">
                  →
                </ArrowButton>
              </ImageSliderWrapper>
            )}
          </ImageGallery>

          <ProductInfo>
            <div>
              <ProductTitle>{product?.name}</ProductTitle>

              <RatingWrapper>
                <span style={{ fontSize: "14px", color: "#666" }}>
                  {(
                    reviews.reduce((sum, r) => sum + r.rating, 0) /
                    (reviews.length || 1)
                  ).toFixed(1)}
                </span>
                <Stars>
                  <Rating
                    initialRating={
                      reviews.length > 0
                        ? reviews.reduce((sum, r) => sum + r.rating, 0) /
                          reviews.length
                        : 0
                    }
                    readonly
                    fractions={2}
                    stop={5}
                    emptySymbol={
                      <span
                        style={{
                          fontSize: "17px",
                          color: "#ddd",
                          marginRight: "1px",
                        }}
                      >
                        ☆
                      </span>
                    }
                    fullSymbol={
                      <span
                        style={{
                          fontSize: "17px",
                          color: "#ffc107",
                          marginRight: "1px",
                        }}
                      >
                        ★
                      </span>
                    }
                  />
                </Stars>

                <span style={{ fontSize: "14px", color: "#666" }}>
                  (
                  {`${reviews.length} ${reviews.length === 1 ? "review" : "reviews"}`}
                  ){" "}
                </span>
              </RatingWrapper>
            </div>

            <MetaInfo>
              <MetaItem>
                Brand : <span>{product?.brand || "N/A"}</span>
              </MetaItem>
              {/* <MetaItem>
                SKU : <span>{product?.id || "N/A"}</span>
              </MetaItem> */}
            </MetaInfo>

            <Description
              dangerouslySetInnerHTML={{
                __html:
                  product?.short_description ||
                  "Product description not available",
              }}
            />

            <SelectorWrapper>
              <SelectorGroup>
                <SelectorLabel>Size : {selectedSize}</SelectorLabel>
                <SizeSelector>
                  {availableSizes.map((size) => (
                    <SizeButton
                      key={size}
                      $active={selectedSize === size}
                      onClick={() => {
                        setSelectedSize(size);
                        const firstColor = product?.variants[size]?.[0];
                        if (firstColor) {
                          setSelectedColorId(firstColor.id);
                        }
                      }}
                    >
                      {size}
                    </SizeButton>
                  ))}
                </SizeSelector>
              </SelectorGroup>

              <SelectorGroup>
                <SelectorLabel>Color</SelectorLabel>
                <ColorSelector>
                  {product?.variants?.[selectedSize]?.map((variant, idx) => (
                    <div
                      key={variant.id}
                      style={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ColorButton
                          $color={`#${variant.color_hex_code?.replace("#", "")}`}
                          $active={selectedColorId === variant.id}
                          onClick={() => setSelectedColorId(variant.id)}
                          title={variant.color_name}
                          style={{
                            cursor: "pointer",
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#666",
                          textAlign: "center",
                        }}
                      >
                        {variant.color_name}
                      </span>
                    </div>
                  ))}
                </ColorSelector>
              </SelectorGroup>
            </SelectorWrapper>

            <PriceWrapper>
              <Price>
                ₹
                {product?.variants?.[selectedSize]?.find(
                  (v) => v.id === selectedColorId,
                )?.price || product?.base_price}
              </Price>
              <OriginalPrice></OriginalPrice>
            </PriceWrapper>

            <DeliveryInfo>
              {product?.variants?.[selectedSize]?.find(
                (v) => v.id === selectedColorId,
              )?.stock_qty === 0 ? (
                ""
              ) : (
                <>
                  Delivered within 2-3 days | Stock:{" "}
                  {product?.variants?.[selectedSize]?.find(
                    (v) => v.id === selectedColorId,
                  )?.stock_qty || 0}{" "}
                  available
                </>
              )}
            </DeliveryInfo>

            <QuantityWrapper>
              <QuantityControl>
                <QuantityButton
                  onClick={() => handleQuantityChange(quantity - 1)}
                >
                  <MinusOutlined style={{ fontSize: "18px" }} />
                </QuantityButton>
                <QuantityInput
                  type="number"
                  value={quantity}
                  onChange={(e) =>
                    handleQuantityChange(parseInt(e.target.value))
                  }
                />
                <QuantityButton
                  onClick={() => handleQuantityChange(quantity + 1)}
                >
                  <PlusOutlined style={{ fontSize: "18px" }} />
                </QuantityButton>
              </QuantityControl>
              <AddToCartButton
                onClick={handleAddToCart}
                disabled={
                  isCartLoading ||
                  !selectedColorId ||
                  product?.variants?.[selectedSize]?.find(
                    (v) => v.id === selectedColorId,
                  )?.stock_qty === 0
                }
              >
                {isCartLoading ? (
                  <>
                    <LoaderSpinner />
                    ADDING...
                  </>
                ) : product?.variants?.[selectedSize]?.find(
                    (v) => v.id === selectedColorId,
                  )?.stock_qty === 0 ? (
                  "OUT OF STOCK"
                ) : (
                  <>
                    <ShoppingCartOutlined style={{ fontSize: "18px" }} />
                    ADD TO CART
                  </>
                )}
              </AddToCartButton>
            </QuantityWrapper>

            {cartMessage && (
              <CartMessage $type={cartMessageType}>{cartMessage}</CartMessage>
            )}

            <ActionButtons>
              <ActionButton
                onClick={
                  isWishlisted ? handleRemoveFromWishlist : handleAddToWishlist
                }
                disabled={isWishlistLoading}
                style={{
                  borderColor: isWishlisted ? "#fd8e07" : "#ddd",
                  color: isWishlisted ? "#fd8e07" : "#000",
                }}
              >
                <HeartOutlined
                  style={{
                    fontSize: "16px",
                    fill: isWishlisted ? "#fd8e07" : "none",
                  }}
                />
                {isWishlistLoading
                  ? "LOADING..."
                  : isWishlisted
                    ? "REMOVE FROM WISHLIST"
                    : "ADD TO WISHLIST"}
              </ActionButton>
            </ActionButtons>
            {/* 
            <PolicySection>
              <PolicyItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <div>
                  <h4>Security policy</h4>
                </div>
              </PolicyItem>

              <PolicyItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <div>
                  <h4>Delivery policy</h4>
                </div>
              </PolicyItem>

              <PolicyItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                <div>
                  <h4>Return policy</h4>
                </div>
              </PolicyItem>
            </PolicySection> */}
          </ProductInfo>
        </ProductContent>

        <TabsWrapper>
          <TabsHeader>
            <Tab
              $active={activeTab === "description"}
              onClick={() => setActiveTab("description")}
            >
              Description
            </Tab>
            {/* <Tab
              $active={activeTab === "details"}
              onClick={() => setActiveTab("details")}
            >
              Product Details
            </Tab> */}
            <Tab
              $active={activeTab === "shipping"}
              onClick={() => setActiveTab("shipping")}
            >
              Shipping
            </Tab>
            <Tab
              $active={activeTab === "sizechart"}
              onClick={() => setActiveTab("sizechart")}
            >
              Size Chart
            </Tab>
          </TabsHeader>

          <TabContent $active={activeTab === "description"}>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  product?.description || "Product description not available",
              }}
            />
          </TabContent>

          <TabContent $active={activeTab === "details"}>
            <p>
              <strong>Product Name:</strong> {product?.name}
            </p>
            <p>
              <strong>Brand:</strong> {product?.brand}
            </p>
            <p>
              <strong>Category:</strong> {product?.category?.name}
            </p>
            <p>
              <strong>Gender:</strong> {product?.gender}
            </p>
            <p>
              <strong>Base Price:</strong> ${product?.base_price}
            </p>
          </TabContent>

          <TabContent $active={activeTab === "shipping"}>
            <p>Shipping Information:</p>
            <ul>
              <li>Standard shipping: 2-3 business days</li>
              <li>Express shipping: 1 business day</li>
              <li>Free shipping on orders over ₹499</li>
            </ul>
          </TabContent>

          <TabContent $active={activeTab === "sizechart"}>
            <p>Available Sizes:</p>
            <ul>
              {availableSizes.map((size) => (
                <li key={size}>
                  Size {size}: {product?.variants[size].length} color(s)
                  available
                </li>
              ))}
            </ul>
          </TabContent>
        </TabsWrapper>

        <ReviewsSection>
          <ReviewsHeader>
            <div>
              <h2>Customer Reviews</h2>
              <p
                style={{
                  margin: "10px 0 0 0",
                  color: "#999",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              >
                {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
              </p>
            </div>
            {/* <WriteReviewButton onClick={() => setIsReviewModalOpen(true)}>
              WRITE YOUR REVIEW
            </WriteReviewButton> */}
          </ReviewsHeader>

          {reviews.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#999",
              }}
            >
              <p style={{ fontSize: "14px" }}>
                No reviews yet. Be the first to review this product!
              </p>
            </div>
          ) : (
            <>
              {reviews.slice(0, 4).map((review) => (
                <ReviewItem key={review.id}>
                  <ReviewMeta>
                    <div>
                      <ReviewRating>
                        {[...Array(5)].map((_, i) => (
                          <span key={i}>{i < review.rating ? "★" : "☆"}</span>
                        ))}
                      </ReviewRating>
                      <ReviewLabel style={{ marginTop: "6px" }}>
                        Verified Purchase
                      </ReviewLabel>
                    </div>
                  </ReviewMeta>

                  <ReviewContent>
                    <ReviewText>{review.comment}</ReviewText>

                    {review.images && review.images.length > 0 && (
                      <ReviewImagesContainer>
                        {review.images.map((image, idx) => (
                          <ReviewImage
                            key={idx}
                            src={image.url}
                            alt={`Review image ${idx + 1}`}
                            onClick={() => openImagePreview(review.images, idx)}
                          />
                        ))}
                      </ReviewImagesContainer>
                    )}

                    <ReviewAuthor>
                      <AuthorAvatar>
                        {review.user?.name?.[0]?.toUpperCase() || "U"}
                      </AuthorAvatar>
                      <AuthorInfo>
                        <p>{review.user?.name || "Anonymous"}</p>
                        <span>
                          {new Date(review.created_at).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </AuthorInfo>
                      {userId === review.user?.id && (
                        <DeleteReviewButton
                          onClick={() =>
                            handleDeleteReview(review.id, product.id)
                          }
                          disabled={isDeletingReview === review.id}
                          title="Delete review"
                        >
                          <DeleteOutlined />
                          Delete
                        </DeleteReviewButton>
                      )}
                    </ReviewAuthor>
                  </ReviewContent>
                </ReviewItem>
              ))}

              {reviews.length > 4 && (
                <ShowAllReviewsButton
                  onClick={() => setShowAllReviewsModal(true)}
                >
                  Show All {reviews.length} Reviews
                </ShowAllReviewsButton>
              )}
            </>
          )}
        </ReviewsSection>

        <RelatedSection>
          {/* <SectionTitle>You might also like</SectionTitle>
          <RelatedProducts>
            {relatedProducts.map((product) => (
              <ProductCard key={product.id} data-aos="fade-up">
                <ProductCardImage>
                  {product.badge && (
                    <div
                      style={{
                        position: "absolute",
                        top: "10px",
                        left: "10px",
                        background: "#000",
                        color: "#fff",
                        padding: "4px 12px",
                        fontSize: "11px",
                        fontWeight: "700",
                        zIndex: 10,
                      }}
                    >
                      {product.badge}
                    </div>
                  )}
                  <img src={product.image} alt={product.title} />
                </ProductCardImage>
                <ProductCardInfo>
                  <ProductCardTitle>{product.title}</ProductCardTitle>
                  <ProductCardRating>
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>{i < product.rating ? "★" : "☆"}</span>
                    ))}
                  </ProductCardRating>
                  <ProductCardPrice>{product.price}</ProductCardPrice>
                </ProductCardInfo>
              </ProductCard>
            ))}
          </RelatedProducts> */}
          <ProductSection
            title="You might also like"
            subtitle=""
            apiEndpoint={`${process.env.REACT_APP_API_BASE_URL}/products/random?limit=4`}
            excludeProductId={product?.id}
            background="#f8f9fa"
          />
        </RelatedSection>
      </ProductWrapper>

      <ModalOverlay $isOpen={isReviewModalOpen}>
        <ModalContent>
          <ModalHeader>
            <h3>Write Your Review</h3>
            <CloseButton onClick={() => setIsReviewModalOpen(false)}>
              ✕
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <FormLabel>Rating</FormLabel>
            <RatingSelector>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={reviewRating >= star ? "active" : ""}
                  onClick={() => setReviewRating(star)}
                >
                  ★
                </span>
              ))}
            </RatingSelector>
          </FormGroup>

          <FormGroup>
            <FormLabel>Review</FormLabel>
            <ReviewTextarea
              placeholder="Share your experience with this product... (minimum 10 characters)"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Add Images</FormLabel>
            <ImageUploadSection>
              <ImageUploadButton htmlFor="review-images">
                📷 Click to add images
              </ImageUploadButton>
              <ImageUploadInput
                id="review-images"
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUploadChange}
              />
              {reviewImages.length > 0 && (
                <>
                  <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
                    {reviewImages.length} image(s) added
                  </p>
                  <ImagePreviewContainer>
                    {reviewImages.map((img, idx) => (
                      <ImagePreview key={idx}>
                        <img
                          src={URL.createObjectURL(img)}
                          alt={`Preview ${idx + 1}`}
                        />
                        <RemoveImageButton
                          onClick={() => handleRemoveImage(idx)}
                          type="button"
                        >
                          ✕
                        </RemoveImageButton>
                      </ImagePreview>
                    ))}
                  </ImagePreviewContainer>
                </>
              )}
            </ImageUploadSection>
          </FormGroup>

          <ModalButtonGroup>
            <CancelButton onClick={() => setIsReviewModalOpen(false)}>
              Cancel
            </CancelButton>
            <SubmitButton
              onClick={handleSubmitReview}
              disabled={isSubmittingReview || !reviewText.trim()}
            >
              {isSubmittingReview ? "Submitting..." : "Submit Review"}
            </SubmitButton>
          </ModalButtonGroup>
        </ModalContent>
      </ModalOverlay>

      <Modal
        title="All Reviews"
        open={showAllReviewsModal}
        onCancel={() => setShowAllReviewsModal(false)}
        footer={null}
        width={700}
        bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        {reviews.map((review) => (
          <ReviewItem key={review.id} style={{ marginBottom: "20px" }}>
            <ReviewMeta>
              <div>
                <ReviewRating>
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>{i < review.rating ? "★" : "☆"}</span>
                  ))}
                </ReviewRating>
                <ReviewLabel style={{ marginTop: "6px" }}>
                  Verified Purchase
                </ReviewLabel>
              </div>
            </ReviewMeta>

            <ReviewContent>
              <ReviewText>{review.comment}</ReviewText>

              {review.images && review.images.length > 0 && (
                <ReviewImagesContainer>
                  {review.images.map((image, idx) => (
                    <ReviewImage
                      key={idx}
                      src={image.url}
                      alt={`Review image ${idx + 1}`}
                      onClick={() => openImagePreview(review.images, idx)}
                    />
                  ))}
                </ReviewImagesContainer>
              )}

              <ReviewAuthor>
                <AuthorAvatar>
                  {review.user?.name?.[0]?.toUpperCase() || "U"}
                </AuthorAvatar>
                <AuthorInfo>
                  <p>{review.user?.name || "Anonymous"}</p>
                  <span>
                    {new Date(review.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </AuthorInfo>
                {userId === review.user?.id && (
                  <DeleteReviewButton
                    onClick={() => handleDeleteReview(review.id, product.id)}
                    disabled={isDeletingReview === review.id}
                    title="Delete review"
                  >
                    <DeleteOutlined />
                    Delete
                  </DeleteReviewButton>
                )}
              </ReviewAuthor>
            </ReviewContent>
          </ReviewItem>
        ))}
      </Modal>

      <Modal
        title="Review Images"
        open={isImagePreviewOpen}
        onCancel={() => setIsImagePreviewOpen(false)}
        footer={null}
        width={700}
        bodyStyle={{ padding: "24px", background: "#fff" }}
      >
        {previewedReviewImages.length > 0 && (
          <SlideshowContainer>
            <div className="slideshow-wrapper">
              <img
                src={previewedReviewImages[selectedImageIndex]?.url}
                alt={`Image ${selectedImageIndex + 1}`}
                className="slide-image"
              />
              {previewedReviewImages.length > 1 && (
                <>
                  <button
                    className="nav-buttons nav-prev"
                    onClick={() =>
                      setSelectedImageIndex(
                        selectedImageIndex === 0
                          ? previewedReviewImages.length - 1
                          : selectedImageIndex - 1,
                      )
                    }
                  >
                    ❮
                  </button>
                  <button
                    className="nav-buttons nav-next"
                    onClick={() =>
                      setSelectedImageIndex(
                        selectedImageIndex === previewedReviewImages.length - 1
                          ? 0
                          : selectedImageIndex + 1,
                      )
                    }
                  >
                    ❯
                  </button>
                </>
              )}
            </div>
            {previewedReviewImages.length > 1 && (
              <div
                className="dot-indicators"
                style={{ position: "absolute", bottom: "15px", width: "100%" }}
              >
                {previewedReviewImages.map((_, idx) => (
                  <div
                    key={idx}
                    className={`dot ${idx === selectedImageIndex ? "active" : ""}`}
                    onClick={() => setSelectedImageIndex(idx)}
                  />
                ))}
              </div>
            )}
          </SlideshowContainer>
        )}
      </Modal>

      <Footer />
    </Container>
  );
};

export default ProductDetail;
